/**
 * Agent execution log prefixes emitted by the pipeline engine.
 *
 * Shown in the playground trace (after filterAgentExecutionLogs):
 * - agent.init, agent.input_added, agent.input_message
 * - agent.input_guardrail(s), agent.output_guardrail
 * - agent.tool_call, agent.error, agent.mcp_error
 *
 * Emitted by the engine but omitted from the playground trace:
 * - agent.output_message (verbose LLM payload)
 * - agent.mcp_tools_loaded (success-only; no dedicated card)
 */

import {
  extractJsonObjectAt,
  stripAgentOutputMessagePrefix
} from '~/entities/agentLogMarkers';

/**
 * Parsed configuration from agent.init log entry
 */
export interface AgentInitConfig {
  provider?: string;
  model?: string;
  temperature?: string;
  max_tokens?: string;
  [key: string]: string | undefined;
}

/**
 * Checks if a log message is an agent.init log entry (case-insensitive)
 */
export function isAgentInitLog(logMessage: string): boolean {
  return logMessage.toLowerCase().includes('agent.init');
}

/**
 * Parses configuration parameters from an agent.init log message
 *
 * Expected formats:
 * - "agent.init - Starting Agent execution: provider=openai, model=gpt-40, ..."
 * - "agent.init - Starting Agent: provider=openai, model=gpt-4o, ..."
 *
 * @param logMessage The log message to parse
 * @returns Parsed configuration object or null if parsing fails
 */
export function parseAgentInitConfig(
  logMessage: string
): AgentInitConfig | null {
  if (!isAgentInitLog(logMessage)) {
    return null;
  }

  try {
    const prefixMatch = logMessage.match(
      /agent\.init\s*-\s*Starting Agent(?: execution)?:\s*(.*)/i
    );
    if (!prefixMatch) {
      return null;
    }
    const configString = prefixMatch[1].trim();

    if (!configString) {
      return null;
    }

    // Parse key-value pairs separated by commas
    // Note: This assumes values don't contain commas. If the format changes to include
    // quoted values or complex structures, this parser will need to be updated.
    const config: AgentInitConfig = {};
    const pairs = configString.split(',');

    pairs
      .map(pair => pair.trim())
      .filter(trimmedPair => trimmedPair.length > 0)
      .forEach(trimmedPair => {
        const equalIndex = trimmedPair.indexOf('=');

        if (equalIndex !== -1 && equalIndex !== 0) {
          const key = trimmedPair.substring(0, equalIndex).trim();
          const value = trimmedPair.substring(equalIndex + 1).trim();

          // Only add if both key and value are non-empty
          if (key && value) {
            config[key] = value;
          }
        }
      });

    // Return null if no valid pairs were found
    return Object.keys(config).length > 0 ? config : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing agent.init config:', error);
    return null;
  }
}

/**
 * Formats a configuration key name for display (snake_case → title case words).
 * Example: "max_tokens" -> "Max Tokens"
 */
export function formatConfigKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map(word => {
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * Parsed configuration from agent.input_added log entry
 */
export interface AgentInputAddedConfig {
  tools: string[];
  added_files: string[];
  content_retrieval: boolean;
}

/**
 * Checks if a log message is an agent.input_added log entry (case-insensitive)
 */
export function isAgentInputAddedLog(logMessage: string): boolean {
  return logMessage.toLowerCase().includes('agent.input_added');
}

/**
 * Parses an array string like "[item1, item2, item3]" into an array of strings
 * Note: This assumes array items don't contain commas. If items can contain commas
 * (e.g., quoted strings), this parser will need to be updated.
 */
function parseArrayString(arrayString: string): string[] {
  try {
    // Remove brackets and trim
    const content = arrayString.trim();
    if (!content.startsWith('[') || !content.endsWith(']')) {
      return [];
    }

    const innerContent = content.slice(1, -1).trim();
    if (!innerContent) {
      return [];
    }

    // Split by comma and trim each item, filtering out empty strings
    return innerContent
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing array string:', error);
    return [];
  }
}

/**
 * Parses configuration parameters from an agent.input_added log message
 *
 * Expected format: "agent.input_added - Including on Agent context: tools=[tavily_search, tavily_extract], added_files=[file2, file1], content_retrieval=false"
 *
 * @param logMessage The log message to parse
 * @returns Parsed configuration object or null if parsing fails
 */
export function parseAgentInputAddedConfig(
  logMessage: string
): AgentInputAddedConfig | null {
  if (!isAgentInputAddedLog(logMessage)) {
    return null;
  }

  try {
    // Extract the part after "agent.input_added - Including on Agent context:" (case-insensitive)
    const prefixMatch = logMessage.match(
      /agent\.input_added\s*-\s*Including on Agent context:\s*(.*)/i
    );
    if (!prefixMatch) {
      return null;
    }
    const configString = prefixMatch[1].trim();

    if (!configString) {
      return null;
    }

    const config: AgentInputAddedConfig = {
      tools: [],
      added_files: [],
      content_retrieval: false
    };

    // Parse tools array
    const toolsMatch = configString.match(/tools=\[([^\]]*)\]/);
    if (toolsMatch && toolsMatch[1]) {
      config.tools = parseArrayString(`[${toolsMatch[1]}]`);
    }

    // Parse added_files array
    const filesMatch = configString.match(/added_files=\[([^\]]*)\]/);
    if (filesMatch && filesMatch[1]) {
      config.added_files = parseArrayString(`[${filesMatch[1]}]`);
    }

    // Parse content_retrieval boolean
    const contentRetrievalMatch = configString.match(
      /content_retrieval=(true|false)/
    );
    if (contentRetrievalMatch) {
      config.content_retrieval = contentRetrievalMatch[1] === 'true';
    }

    return config;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing agent.input_added config:', error);
    return null;
  }
}

/**
 * Parsed configuration from agent.input_message log entry
 */
export interface AgentInputMessageConfig {
  messageType: 'system' | 'user';
  content: string;
}

/** BOM / ZW* so `^System|User Message=` anchors after trim. */
const LEADING_INVISIBLE_CHARS = /^[\uFEFF\u200B-\u200D]+/gu;

/**
 * `[\s\S]` includes newlines; `.` alone does not — required for JSON / multiline payloads.
 */
const AGENT_INPUT_MESSAGE_LABEL = {
  anchored: {
    system: /^System Message\s*=\s*([\s\S]*)/i,
    user: /^User Message\s*=\s*([\s\S]*)/i
  },
  loose: {
    system: /System Message\s*=\s*([\s\S]*)/i,
    user: /User Message\s*=\s*([\s\S]*)/i
  }
} as const;

type AgentInputMessageRole = keyof typeof AGENT_INPUT_MESSAGE_LABEL.anchored;

type AgentInputMessageLabelMatch = {
  content: string;
  /** Start index of the full match in `configString` (for picking earliest label). */
  index: number;
};

function normalizeAgentInputMessagePayload(raw: string): string {
  return raw.trim().replace(LEADING_INVISIBLE_CHARS, '').trim();
}

function matchAgentInputMessageLabel(
  configString: string,
  role: AgentInputMessageRole,
  mode: 'anchored' | 'loose'
): AgentInputMessageLabelMatch | null {
  const m = configString.match(AGENT_INPUT_MESSAGE_LABEL[mode][role]);
  if (!m) {
    return null;
  }
  return {
    content: m[1].trim(),
    index: m.index ?? 0
  };
}

/**
 * Checks if a log message is an agent.input_message log entry (case-insensitive)
 */
export function isAgentInputMessageLog(logMessage: string): boolean {
  return logMessage.toLowerCase().includes('agent.input_message');
}

/**
 * Parses message type and content from an agent.input_message log message
 *
 * Expected format: "agent.input_message - Message added on llm context: System Message=I am a system message."
 * or: "agent.input_message - Message added on llm context: User Message=I am an user message."
 * Content may span multiple lines (e.g. JSON after a title line); parsing must capture everything after "=".
 *
 * @param logMessage The log message to parse
 * @returns Parsed configuration object or null if parsing fails
 */
export function parseAgentInputMessageConfig(
  logMessage: string
): AgentInputMessageConfig | null {
  if (!isAgentInputMessageLog(logMessage)) {
    return null;
  }

  try {
    // Extract the part after "agent.input_message - Message added on llm context:" (case-insensitive)
    const prefixMatch = logMessage.match(
      /agent\.input_message\s*-\s*Message added on llm context:\s*([\s\S]*)/i
    );
    if (!prefixMatch) {
      return null;
    }

    const configString = normalizeAgentInputMessagePayload(prefixMatch[1]);

    if (!configString) {
      return null;
    }

    const systemAtStart = matchAgentInputMessageLabel(
      configString,
      'system',
      'anchored'
    );
    const userAtStart = matchAgentInputMessageLabel(
      configString,
      'user',
      'anchored'
    );

    let messageType: 'system' | 'user' | null = null;
    let content = '';

    if (systemAtStart) {
      messageType = 'system';
      content = systemAtStart.content;
    } else if (userAtStart) {
      messageType = 'user';
      content = userAtStart.content;
    } else {
      // Payload still did not start with a label (unusual spacing, unexpected prefix).
      // Use the earliest "System|User Message=" in the string so multiline content stays intact.
      const looseSystem = matchAgentInputMessageLabel(
        configString,
        'system',
        'loose'
      );
      const looseUser = matchAgentInputMessageLabel(
        configString,
        'user',
        'loose'
      );
      const sysIdx = looseSystem?.index ?? -1;
      const usrIdx = looseUser?.index ?? -1;

      if (looseSystem && (usrIdx < 0 || sysIdx <= usrIdx)) {
        messageType = 'system';
        content = looseSystem.content;
      } else if (looseUser) {
        messageType = 'user';
        content = looseUser.content;
      }
    }

    if (!messageType || !content) {
      return null;
    }

    return {
      messageType,
      content
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing agent.input_message config:', error);
    return null;
  }
}

/**
 * Parsed configuration from guardrail log entry
 */
export interface GuardrailConfig {
  type: 'input' | 'output';
  guardrailType: string;
  status: 'success' | 'failed';
  message: string;
}

/**
 * Checks if a log message is a guardrail log entry (case-insensitive)
 * Note: agent.error logs are NOT considered guardrail logs - they are handled separately
 */
export function isGuardrailLog(logMessage: string): boolean {
  const lower = logMessage.toLowerCase();
  return (
    lower.includes('agent.input_guardrail') ||
    lower.includes('agent.input_guardrails') ||
    lower.includes('agent.output_guardrail')
  );
}

/**
 * Formats guardrail type name for display (consistent title-style labels).
 */
export function formatGuardrailType(guardrailType: string): string {
  const lower = guardrailType.toLowerCase();
  if (lower.includes('pii')) {
    return 'PII check';
  }
  if (lower.includes('json schema') || lower.includes('jsonschema')) {
    return 'Schema check';
  }
  if (lower.includes('regex')) {
    return 'Regex check';
  }
  const cleaned = guardrailType.replace(/Guardrail/gi, '').trim();
  if (!cleaned) {
    return guardrailType;
  }
  return cleaned
    .split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Parses guardrail information from a guardrail log message
 *
 * Expected format: "agent.input_guardrail - PII Guardrail: {status: fail, message=Email failed, URL failed...}"
 * or: "agent.input_guardrail - PII Guardrail: {status: success, message=All rules passed.}"
 * or: "agent.error - Error when calling LLM provider: The guardrail ... failed with this message: ..."
 *
 * @param logMessage The log message to parse
 * @returns Parsed configuration object or null if parsing fails
 */
export function parseGuardrailConfig(
  logMessage: string
): GuardrailConfig | null {
  // Only parse standard guardrail log entries (agent.input_guardrail, agent.output_guardrail)
  // agent.error logs are NOT parsed as guardrail logs - they are handled separately
  const lower = logMessage.toLowerCase();
  const isGuardrailLogEntry =
    lower.includes('agent.input_guardrail') ||
    lower.includes('agent.input_guardrails') ||
    lower.includes('agent.output_guardrail');

  if (!isGuardrailLogEntry) {
    return null;
  }

  try {
    // Handle standard guardrail logs
    // Determine if it's input or output guardrail
    const isInput =
      lower.includes('agent.input_guardrail') ||
      lower.includes('agent.input_guardrails');
    const type: 'input' | 'output' = isInput ? 'input' : 'output';

    // Strip the "agent.input_guardrail - " or "agent.output_guardrail - " prefix (case-insensitive)
    const inputGuardrailIdx = lower.indexOf('agent.input_guardrail');
    const outputGuardrailIdx = lower.indexOf('agent.output_guardrail');
    const inputGuardrailsIdx = lower.indexOf('agent.input_guardrails');
    let messageToParse = logMessage;
    if (inputGuardrailIdx !== -1) {
      messageToParse = logMessage
        .slice(inputGuardrailIdx)
        .replace(/^agent\.input_guardrail\s*-\s*/i, '');
    } else if (outputGuardrailIdx !== -1) {
      messageToParse = logMessage
        .slice(outputGuardrailIdx)
        .replace(/^agent\.output_guardrail\s*-\s*/i, '');
    } else if (inputGuardrailsIdx !== -1) {
      messageToParse = logMessage
        .slice(inputGuardrailsIdx)
        .replace(/^agent\.input_guardrails\s*-\s*/i, '');
    }

    // Extract guardrail information
    // Pattern: "GuardrailType: {status: success|failed, message=...}"
    // Note: Backend uses "fail" instead of "failed" in some cases
    const guardrailMatch = messageToParse.match(
      /([^:]+):\s*\{status:\s*(success|failed|fail),\s*message=([^}]+)\}/
    );

    if (!guardrailMatch) {
      return null;
    }

    const guardrailType = guardrailMatch[1].trim();
    const statusStr = guardrailMatch[2].trim().toLowerCase();
    const status =
      statusStr === 'fail' ? 'failed' : (statusStr as 'success' | 'failed');
    const message = guardrailMatch[3].trim();

    if (!guardrailType || !status || !message) {
      return null;
    }

    return {
      type,
      guardrailType,
      status,
      message
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing guardrail config:', error);
    return null;
  }
}

/**
 * Parsed configuration from agent.tool_call log entry
 */
export interface AgentToolCallConfig {
  toolName: string;
  params: string;
  output: string;
}

/**
 * Checks if a log message is an agent.tool_call log entry (case-insensitive)
 */
export function isAgentToolCallLog(logMessage: string): boolean {
  return logMessage.toLowerCase().includes('agent.tool_call');
}

/**
 * Formats a JSON string with proper indentation
 */
function formatJSONString(jsonString: string): string {
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // If not valid JSON, return as-is
    return jsonString;
  }
}

/**
 * Parses tool call information from an agent.tool_call log message
 *
 * Expected format: "agent.tool_call - Tool invoked: name=tool_name, params={...}, output={...}"
 *
 * @param logMessage The log message to parse
 * @returns Parsed configuration object or null if parsing fails
 */
export function parseAgentToolCallConfig(
  logMessage: string
): AgentToolCallConfig | null {
  if (!isAgentToolCallLog(logMessage)) {
    return null;
  }

  try {
    // Extract the part after "agent.tool_call - Tool invoked:" (case-insensitive).
    // Use [\s\S]* so the capture includes newlines (tool output can be multiline).
    const prefixMatch = logMessage.match(
      /agent\.tool_call\s*-\s*Tool invoked:\s*([\s\S]*)/i
    );
    if (!prefixMatch) {
      return null;
    }
    const configString = prefixMatch[1].trim();

    if (!configString) {
      return null;
    }

    // Extract tool name
    const nameMatch = configString.match(/name=([^,]+)/);
    if (!nameMatch || !nameMatch[1]) {
      return null;
    }
    const toolName = nameMatch[1].trim();

    // Extract params - find params= and extract the JSON object
    const paramsIndex = configString.indexOf('params=');
    let params = '{}';
    if (paramsIndex !== -1) {
      const jsonStart = paramsIndex + 'params='.length;
      const jsonObject = extractJsonObjectAt(configString, jsonStart);
      if (jsonObject) {
        params = formatJSONString(jsonObject);
      }
    }

    // Extract output - everything after output= (tool response can be JSON or plain text)
    const outputIndex = configString.indexOf('output=');
    let output = '';
    if (outputIndex !== -1) {
      const outputStart = outputIndex + 'output='.length;
      const rawOutput = configString.substring(outputStart).trim();
      if (rawOutput) {
        const jsonObject = extractJsonObjectAt(configString, outputStart);
        output = jsonObject != null ? formatJSONString(jsonObject) : rawOutput;
      }
    }
    if (output === '') {
      output = '{}';
    }

    if (!toolName) {
      return null;
    }

    return {
      toolName,
      params,
      output
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing agent.tool_call config:', error);
    return null;
  }
}

/**
 * Parsed content from agent.output_message log entry (LLM response / final output step)
 */
export interface AgentOutputMessageConfig {
  content: string;
}

/**
 * Checks if a log message is an agent.output_message log entry (case-insensitive)
 */
export function isAgentOutputMessageLog(logMessage: string): boolean {
  return logMessage.toLowerCase().includes('agent.output_message');
}

/**
 * Parses content from an agent.output_message log message.
 * Expected format: "agent.output_message - <content>" or similar; content may be JSON or plain text.
 */
export function parseAgentOutputMessageConfig(
  logMessage: string
): AgentOutputMessageConfig | null {
  if (!isAgentOutputMessageLog(logMessage)) {
    return null;
  }
  const content = stripAgentOutputMessagePrefix(logMessage);
  return { content: content || logMessage };
}

/**
 * Extracts error title and message from agent.error log (same logic as ErrorLogCard in LogsSection).
 */
export function parseAgentError(logMessage: string): {
  title: string;
  message: string;
} {
  let errorMessage = logMessage.replace(/^agent\.error\s*-\s*/i, '');
  let errorTitle = 'Error';

  const guardrailMatch = errorMessage.match(/guardrail\s+([^.]+)\s+failed/i);
  if (guardrailMatch) {
    const guardrailType = guardrailMatch[1].trim();
    errorTitle = `${guardrailType} Guardrail Failed`;

    const messageMatch = errorMessage.match(/message:\s*(.+)$/i);
    if (messageMatch) {
      errorMessage = `Guardrail validation failed: ${messageMatch[1].trim()}`;
    }
  } else if (logMessage.includes('Error when calling LLM provider')) {
    errorTitle = 'LLM Provider Error';
    errorMessage = errorMessage.replace(
      /^Error when calling LLM provider:\s*/i,
      ''
    );
  }

  return { title: errorTitle, message: errorMessage };
}

/**
 * Checks if a log message is an agent.mcp_tools_loaded log entry (case-insensitive).
 * Omitted from the playground trace — no dedicated card.
 */
export function isAgentMcpToolsLoadedLog(logMessage: string): boolean {
  return /^agent\.mcp_tools_loaded\s*-/i.test(logMessage.trim());
}

/**
 * Checks if a log message is an agent.mcp_error log entry (case-insensitive).
 */
export function isAgentMcpErrorLog(logMessage: string): boolean {
  return /^agent\.mcp_error\s*-/i.test(logMessage.trim());
}

/**
 * Checks if a log message is an agent.error log entry (case-insensitive).
 */
export function isAgentGenericErrorLog(logMessage: string): boolean {
  return /^agent\.error\s*-/i.test(logMessage.trim());
}

/**
 * Parses agent.mcp_error logs for display in ErrorLogCard.
 *
 * Expected format:
 * "agent.mcp_error - Failed to load tools from MCP server config: {...}. Error: 401 Unauthorized"
 */
export function parseAgentMcpError(logMessage: string): {
  title: string;
  message: string;
} {
  return {
    title: 'MCP Connection Error',
    message: logMessage.replace(/^agent\.mcp_error\s*-\s*/i, '').trim()
  };
}

/**
 * Returns true for agent.error and agent.mcp_error logs shown in ErrorLogCard.
 */
export function isAgentErrorLog(logMessage: string): boolean {
  return isAgentMcpErrorLog(logMessage) || isAgentGenericErrorLog(logMessage);
}

/**
 * Parses agent.error and agent.mcp_error logs for ErrorLogCard.
 */
export function parseAgentExecutionError(logMessage: string): {
  title: string;
  message: string;
} {
  if (isAgentMcpErrorLog(logMessage)) {
    return parseAgentMcpError(logMessage);
  }
  return parseAgentError(logMessage);
}
